import { Component } from "vue";
import { SectionType } from "@/apps/page/page.interfaces";
import {
    BlockBaseElement,
    BlockText,
    BlockLink,
    BlockButton,
    BlockHeading,
    BlockImage,
    BlockDescriptiveImage,
    BlockBackgroundImage,
    BlockMedia,
    BlockArrayItems,
    BlockDataValue,
    MediaType,
    BlockTestimonial,
} from "@/views/blocks/PageBlock.interface";

import type { HeadingFormatting } from "@/views/blocks/elements/heading-element/HeadingElementProps";
import type { ParagraphFormatting } from "@/views/blocks/elements/paragraph-element/ParagraphElementProps";
import type { MediaFormatting } from "@/views/blocks/elements/media-element/MediaElementProps";
import type { TestimonialFormatting } from "@/views/blocks/elements/testimonial-element/TestimonialElementProps";
import type { SimpleListFormatting } from "@/views/blocks/elements/simple-list-element/SimpleListElementProps";
import type { InfoNoteFormatting } from "@/views/blocks/elements/info-note-element/InfoNoteElementProps";
import type { ButtonsFormatting } from "@/views/blocks/elements/buttons-element/ButtonsElementProps";

import {
    BlockDefinitionField,
    BlockDefinitionGroup,
    BlockDefinitionTextField,
    BlockDefinitionTextAreaField,
    BlockDefinitionHtmlField,
    BlockDefinitionSelectField,
    BlockDefinitionSliderField,
    BlockDefinitionImageField,
    BlockDefinitionVideoField,
    BlockDefinitionSwitchField,
    BlockDefinitionLinkField,
    BlockDef,
    BlockColorField,
} from "./block.interfaces";
import { randomId } from "@/shared/utils/helpers";

interface BlockFieldConfig<T> {
    label: string;
    group: BlockDefinitionGroup;
    fields: BlockDefinitionField[];
    defaultData: BlockBaseElement<T>;
    newItem?: T;
}

function createBlock<T extends Record<string, BlockBaseElement<BlockDataValue>>>(config: {
    typeId: string;
    type: SectionType;
    name: string;
    component: Component;
}) {
    const dataFields: Array<{
        value: string;
        label: string;
        group: BlockDefinitionGroup;
        groupType: "single" | "multiple";
        fields: BlockDefinitionField[];
        defaultData: BlockBaseElement<BlockDataValue>;
    }> = [];

    const styleFields: BlockColorField[] = [];

    return {
        withField<K extends keyof T>(
            key: K,
            field: BlockFieldConfig<
                T[K] extends BlockBaseElement<infer U> ? U & BlockDataValue : never
            >
        ) {
            dataFields.push({
                value: key as string,
                ...field,
                groupType: "single",
            });
            return this;
        },

        withArrayField<K extends keyof T>(
            key: K,
            field: BlockFieldConfig<
                T[K] extends BlockBaseElement<infer U> ? U & BlockDataValue : never
            >
        ) {
            dataFields.push({
                value: key as string,
                ...field,
                groupType: "multiple",
            });
            return this;
        },

        withStyleFields(fields: BlockColorField[]) {
            styleFields.push(...fields);
            return this;
        },

        build(): BlockDef {
            return {
                typeId: config.typeId,
                type: config.type,
                name: config.name,
                // @ts-expect-error TODO BF: fix typing
                dataFields,
                styleFields,
                component: config.component,
            };
        },
    };
}

function textField(
    label: string,
    defaultText: string,
    fieldLabel?: string
): BlockFieldConfig<BlockText> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "text",
                label: fieldLabel ?? label,
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: { text: defaultText },
            isHidden: false,
        },
    };
}

function markdownField(
    label: string,
    defaultText: string,
    fieldLabel: string,
    rows = 10
): BlockFieldConfig<BlockText> {
    return {
        label,
        group: "markdown",
        fields: [
            {
                value: "text",
                type: "textarea",
                label: fieldLabel,
                rows,
            } satisfies BlockDefinitionTextAreaField,
        ],
        defaultData: {
            data: { text: defaultText },
            isHidden: false,
        },
    };
}

function htmlField(
    label: string,
    defaultText: string,
    fieldLabel: string,
    rows = 5
): BlockFieldConfig<BlockText> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "html",
                label: fieldLabel,
                rows,
            } satisfies BlockDefinitionHtmlField,
        ],
        defaultData: {
            data: { text: defaultText },
            isHidden: false,
        },
    };
}

function headingField(
    label: string,
    defaultText: string,
    defaultTag: BlockHeading["tag"] = "h2",
    fieldLabels: {
        text: string;
        tag: string;
    }
): BlockFieldConfig<BlockHeading> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "text",
                label: fieldLabels.text,
            } satisfies BlockDefinitionTextField,
            {
                value: "tag",
                type: "select",
                label: fieldLabels.tag,
                options: [
                    { value: "h1", label: "H1" },
                    { value: "h2", label: "H2" },
                    { value: "h3", label: "H3" },
                    { value: "h4", label: "H4" },
                    { value: "h5", label: "H5" },
                    { value: "h6", label: "H6" },
                ],
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: { text: defaultText, tag: defaultTag },
            isHidden: false,
        },
    };
}

function separateParagraphFormattingField(
    label: string,
    defaultTextSize: ParagraphFormatting["textSize"] = "text-lg",
    defaultTextAlign: ParagraphFormatting["textAlign"] = "text-left",
    defaultMaxWidth: ParagraphFormatting["maxWidth"] = "max-w-2xl",
    defaultElementAlign: ParagraphFormatting["elementAlign"] = "mx-auto",
    defaultPaddingTop: ParagraphFormatting["paddingTop"] = "pt-0",
    defaultPaddingBottom: ParagraphFormatting["paddingBottom"] = "pb-4",
    fieldLabels: {
        textSize: string;
        textAlign: string;
        maxWidth: string;
        elementAlign: string;
        paddingTop: string;
        paddingBottom: string;
    },
    options: {
        textSize: { value: string; label: string }[];
        textAlign: { value: string; label: string }[];
        maxWidth: { value: string; label: string }[];
        elementAlign: { value: string; label: string }[];
        paddingTop: { value: string; label: string }[];
        paddingBottom: { value: string; label: string }[];
    }
): BlockFieldConfig<ParagraphFormatting> {
    return {
        label,
        group: "formatting",
        fields: [
            {
                value: "textSize",
                type: "select",
                label: fieldLabels.textSize,
                options: options.textSize,
            } satisfies BlockDefinitionSelectField,
            {
                value: "textAlign",
                type: "select",
                label: fieldLabels.textAlign,
                options: options.textAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "maxWidth",
                type: "select",
                label: fieldLabels.maxWidth,
                options: options.maxWidth,
            } satisfies BlockDefinitionSelectField,
            {
                value: "elementAlign",
                type: "select",
                label: fieldLabels.elementAlign,
                options: options.elementAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingTop",
                type: "select",
                label: fieldLabels.paddingTop,
                options: options.paddingTop,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingBottom",
                type: "select",
                label: fieldLabels.paddingBottom,
                options: options.paddingBottom,
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: {
                textSize: defaultTextSize,
                textAlign: defaultTextAlign,
                maxWidth: defaultMaxWidth,
                elementAlign: defaultElementAlign,
                paddingTop: defaultPaddingTop,
                paddingBottom: defaultPaddingBottom,
            },
            isHidden: false,
        },
    };
}

function buttonField(
    label: string,
    defaultButton: BlockButton,
    fieldLabels: {
        label: string;
        href: string;
    }
): BlockFieldConfig<BlockButton> {
    return {
        label,
        group: "buttons",
        fields: [
            {
                value: "label",
                type: "text",
                label: fieldLabels.label,
            } satisfies BlockDefinitionTextField,
            {
                value: "href",
                type: "link",
                label: fieldLabels.href,
            } satisfies BlockDefinitionLinkField,
        ],
        defaultData: {
            data: defaultButton,
            isHidden: false,
        },
    };
}

function imageField(
    label: string,
    defaultImage: BlockImage,
    fieldLabel: string
): BlockFieldConfig<BlockImage> {
    return {
        label,
        group: "images",
        fields: [
            {
                value: "src",
                type: "image",
                label: fieldLabel,
            } satisfies BlockDefinitionImageField,
        ],
        defaultData: {
            data: defaultImage,
            isHidden: false,
        },
    };
}

function descriptiveImageField(
    label: string,
    defaultImage: BlockDescriptiveImage,
    fieldLabels: {
        src: string;
        alt: string;
    }
): BlockFieldConfig<BlockDescriptiveImage> {
    return {
        label,
        group: "images",
        fields: [
            {
                value: "src",
                type: "image",
                label: fieldLabels.src,
            } satisfies BlockDefinitionImageField,
            {
                value: "alt",
                type: "text",
                label: fieldLabels.alt,
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: defaultImage,
            isHidden: false,
        },
    };
}

function backgroundImageField(
    label: string,
    defaultImage: BlockBackgroundImage,
    fieldLabels: {
        src: string;
        transparency: string;
    }
): BlockFieldConfig<BlockBackgroundImage> {
    return {
        label,
        group: "images",
        fields: [
            {
                value: "src",
                type: "image",
                label: fieldLabels.src,
            } satisfies BlockDefinitionImageField,
            {
                value: "transparency",
                type: "slider",
                label: fieldLabels.transparency,
                min: 0,
                max: 100,
            } satisfies BlockDefinitionSliderField,
        ],
        defaultData: {
            data: defaultImage,
            isHidden: false,
        },
    };
}

function mediaField(
    label: string,
    defaultMedia: Partial<BlockMedia>,
    fieldLabels: {
        mediaType: string;
        src: string;
        alt: string;
        videoSrc: string;
        videoAutoplay: string;
        videoControls: string;
        videoLoop: string;
        iframeSrc: string;
    }
): BlockFieldConfig<BlockMedia> {
    return {
        label,
        group: "media",
        fields: [
            {
                value: "mediaType",
                type: "select",
                label: fieldLabels.mediaType,
                options: [
                    // Setting labels in GroupMedia.vue
                    { value: MediaType.IMAGE, label: "" },
                    { value: MediaType.VIDEO, label: "" },
                    { value: MediaType.IFRAME, label: "" },
                ],
            } satisfies BlockDefinitionSelectField,
            {
                value: "src",
                type: "image",
                label: fieldLabels.src,
            } satisfies BlockDefinitionImageField,
            {
                value: "alt",
                type: "text",
                label: fieldLabels.alt,
            } satisfies BlockDefinitionTextField,
            {
                value: "videoSrc",
                type: "video",
                label: fieldLabels.videoSrc,
            } satisfies BlockDefinitionVideoField,
            {
                value: "videoAutoplay",
                type: "switch",
                label: fieldLabels.videoAutoplay,
            } satisfies BlockDefinitionSwitchField,
            {
                value: "videoControls",
                type: "switch",
                label: fieldLabels.videoControls,
            } satisfies BlockDefinitionSwitchField,
            {
                value: "videoLoop",
                type: "switch",
                label: fieldLabels.videoLoop,
            } satisfies BlockDefinitionSwitchField,
            {
                value: "iframeSrc",
                type: "text",
                label: fieldLabels.iframeSrc,
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: {
                src: defaultMedia.src || "",
                alt: defaultMedia.alt || "",
                mediaType: defaultMedia.mediaType || MediaType.IMAGE,
                videoSrc: defaultMedia.videoSrc || "",
                videoAutoplay:
                    defaultMedia.videoAutoplay !== undefined ? defaultMedia.videoAutoplay : true,
                videoControls:
                    defaultMedia.videoControls !== undefined ? defaultMedia.videoControls : false,
                videoLoop: defaultMedia.videoLoop !== undefined ? defaultMedia.videoLoop : true,
                iframeSrc: defaultMedia.iframeSrc || "",
            },
            isHidden: false,
        },
    };
}

function separateMediaFormattingField(
    label: string,
    defaultMaxWidth: MediaFormatting["maxWidth"] = "max-w-2xl",
    defaultElementAlign: MediaFormatting["elementAlign"] = "mx-auto",
    defaultPaddingTop: MediaFormatting["paddingTop"] = "pt-4",
    defaultPaddingBottom: MediaFormatting["paddingBottom"] = "pb-4",
    fieldLabels: {
        maxWidth: string;
        elementAlign: string;
        paddingTop: string;
        paddingBottom: string;
        iframeHeight: string;
    },
    options: {
        maxWidth: { value: string; label: string }[];
        elementAlign: { value: string; label: string }[];
        paddingTop: { value: string; label: string }[];
        paddingBottom: { value: string; label: string }[];
    }
): BlockFieldConfig<MediaFormatting> {
    return {
        label,
        group: "formatting",
        fields: [
            {
                value: "maxWidth",
                type: "select",
                label: fieldLabels.maxWidth,
                options: options.maxWidth,
            } satisfies BlockDefinitionSelectField,
            {
                value: "elementAlign",
                type: "select",
                label: fieldLabels.elementAlign,
                options: options.elementAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingTop",
                type: "select",
                label: fieldLabels.paddingTop,
                options: options.paddingTop,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingBottom",
                type: "select",
                label: fieldLabels.paddingBottom,
                options: options.paddingBottom,
            } satisfies BlockDefinitionSelectField,
            {
                value: "iframeHeight",
                type: "slider",
                label: fieldLabels.iframeHeight,
                min: 0,
                max: 10000,
            } satisfies BlockDefinitionSliderField,
        ],
        defaultData: {
            data: {
                maxWidth: defaultMaxWidth,
                elementAlign: defaultElementAlign,
                paddingTop: defaultPaddingTop,
                paddingBottom: defaultPaddingBottom,
                iframeHeight: undefined,
            },
            isHidden: false,
        },
    };
}

function testimonialField(
    label: string,
    defaultTestimonial: BlockTestimonial,
    fieldLabels: {
        text: string;
        personImageSrc: string;
        personName: string;
        personPosition: string;
    }
): BlockFieldConfig<BlockTestimonial> {
    return {
        label,
        group: "testimonial",
        fields: [
            {
                value: "text",
                type: "html",
                label: fieldLabels.text,
                rows: 3,
            } satisfies BlockDefinitionHtmlField,
            {
                value: "personImageSrc",
                type: "image",
                label: fieldLabels.personImageSrc,
            } satisfies BlockDefinitionImageField,
            {
                value: "personName",
                type: "text",
                label: fieldLabels.personName,
            } satisfies BlockDefinitionTextField,
            {
                value: "personPosition",
                type: "text",
                label: fieldLabels.personPosition,
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: defaultTestimonial,
            isHidden: false,
        },
    };
}

function separateTestimonialFormattingField(
    label: string,
    defaultTextSize: TestimonialFormatting["textSize"] = "text-base",
    defaultMaxWidth: TestimonialFormatting["maxWidth"] = "max-w-2xl",
    defaultElementAlign: TestimonialFormatting["elementAlign"] = "mx-auto",
    defaultPaddingTop: TestimonialFormatting["paddingTop"] = "pt-4",
    defaultPaddingBottom: TestimonialFormatting["paddingBottom"] = "pb-4",
    fieldLabels: {
        textSize: string;
        maxWidth: string;
        elementAlign: string;
        paddingTop: string;
        paddingBottom: string;
    },
    options: {
        textSize: { value: string; label: string }[];
        maxWidth: { value: string; label: string }[];
        elementAlign: { value: string; label: string }[];
        paddingTop: { value: string; label: string }[];
        paddingBottom: { value: string; label: string }[];
    }
): BlockFieldConfig<TestimonialFormatting> {
    return {
        label,
        group: "formatting",
        fields: [
            {
                value: "textSize",
                type: "select",
                label: fieldLabels.textSize,
                options: options.textSize,
            } satisfies BlockDefinitionSelectField,
            {
                value: "maxWidth",
                type: "select",
                label: fieldLabels.maxWidth,
                options: options.maxWidth,
            } satisfies BlockDefinitionSelectField,
            {
                value: "elementAlign",
                type: "select",
                label: fieldLabels.elementAlign,
                options: options.elementAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingTop",
                type: "select",
                label: fieldLabels.paddingTop,
                options: options.paddingTop,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingBottom",
                type: "select",
                label: fieldLabels.paddingBottom,
                options: options.paddingBottom,
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: {
                textSize: defaultTextSize,
                maxWidth: defaultMaxWidth,
                elementAlign: defaultElementAlign,
                paddingTop: defaultPaddingTop,
                paddingBottom: defaultPaddingBottom,
            },
            isHidden: false,
        },
    };
}

function separateSimpleListFormattingField(
    label: string,
    defaultTextSize: SimpleListFormatting["textSize"] = "text-base",
    defaultMaxWidth: SimpleListFormatting["maxWidth"] = "max-w-2xl",
    defaultElementAlign: SimpleListFormatting["elementAlign"] = "mx-auto",
    defaultPaddingTop: SimpleListFormatting["paddingTop"] = "pt-4",
    defaultPaddingBottom: SimpleListFormatting["paddingBottom"] = "pb-4",
    fieldLabels: {
        textSize: string;
        maxWidth: string;
        elementAlign: string;
        paddingTop: string;
        paddingBottom: string;
    },
    options: {
        textSize: { value: string; label: string }[];
        maxWidth: { value: string; label: string }[];
        elementAlign: { value: string; label: string }[];
        paddingTop: { value: string; label: string }[];
        paddingBottom: { value: string; label: string }[];
    }
): BlockFieldConfig<SimpleListFormatting> {
    return {
        label,
        group: "formatting",
        fields: [
            {
                value: "textSize",
                type: "select",
                label: fieldLabels.textSize,
                options: options.textSize,
            } satisfies BlockDefinitionSelectField,
            {
                value: "maxWidth",
                type: "select",
                label: fieldLabels.maxWidth,
                options: options.maxWidth,
            } satisfies BlockDefinitionSelectField,
            {
                value: "elementAlign",
                type: "select",
                label: fieldLabels.elementAlign,
                options: options.elementAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingTop",
                type: "select",
                label: fieldLabels.paddingTop,
                options: options.paddingTop,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingBottom",
                type: "select",
                label: fieldLabels.paddingBottom,
                options: options.paddingBottom,
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: {
                textSize: defaultTextSize,
                maxWidth: defaultMaxWidth,
                elementAlign: defaultElementAlign,
                paddingTop: defaultPaddingTop,
                paddingBottom: defaultPaddingBottom,
            },
            isHidden: false,
        },
    };
}

function separateInfoNoteFormattingField(
    label: string,
    defaultTextSize: InfoNoteFormatting["textSize"] = "text-sm",
    defaultJustifyContent: InfoNoteFormatting["justifyContent"] = "justify-start",
    defaultMaxWidth: InfoNoteFormatting["maxWidth"] = "max-w-2xl",
    defaultElementAlign: InfoNoteFormatting["elementAlign"] = "mx-auto",
    defaultPaddingTop: InfoNoteFormatting["paddingTop"] = "pt-0",
    defaultPaddingBottom: InfoNoteFormatting["paddingBottom"] = "pb-4",
    fieldLabels: {
        textSize: string;
        justifyContent: string;
        maxWidth: string;
        elementAlign: string;
        paddingTop: string;
        paddingBottom: string;
    },
    options: {
        textSize: { value: string; label: string }[];
        justifyContent: { value: string; label: string }[];
        maxWidth: { value: string; label: string }[];
        elementAlign: { value: string; label: string }[];
        paddingTop: { value: string; label: string }[];
        paddingBottom: { value: string; label: string }[];
    }
): BlockFieldConfig<InfoNoteFormatting> {
    return {
        label,
        group: "formatting",
        fields: [
            {
                value: "textSize",
                type: "select",
                label: fieldLabels.textSize,
                options: options.textSize,
            } satisfies BlockDefinitionSelectField,
            {
                value: "justifyContent",
                type: "select",
                label: fieldLabels.justifyContent,
                options: options.justifyContent,
            } satisfies BlockDefinitionSelectField,
            {
                value: "maxWidth",
                type: "select",
                label: fieldLabels.maxWidth,
                options: options.maxWidth,
            } satisfies BlockDefinitionSelectField,
            {
                value: "elementAlign",
                type: "select",
                label: fieldLabels.elementAlign,
                options: options.elementAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingTop",
                type: "select",
                label: fieldLabels.paddingTop,
                options: options.paddingTop,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingBottom",
                type: "select",
                label: fieldLabels.paddingBottom,
                options: options.paddingBottom,
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: {
                textSize: defaultTextSize,
                justifyContent: defaultJustifyContent,
                maxWidth: defaultMaxWidth,
                elementAlign: defaultElementAlign,
                paddingTop: defaultPaddingTop,
                paddingBottom: defaultPaddingBottom,
            },
            isHidden: false,
        },
    };
}

function separateHeadingFormattingField(
    label: string,
    defaultTextSize: HeadingFormatting["textSize"] = "text-4xl sm:text-5xl",
    defaultTextAlign: HeadingFormatting["textAlign"] = "text-left",
    defaultMaxWidth: HeadingFormatting["maxWidth"] = "max-w-2xl",
    defaultElementAlign: HeadingFormatting["elementAlign"] = "mx-auto",
    defaultPaddingTop: HeadingFormatting["paddingTop"] = "pt-4",
    defaultPaddingBottom: HeadingFormatting["paddingBottom"] = "pb-4",
    fieldLabels: {
        textSize: string;
        textAlign: string;
        maxWidth: string;
        elementAlign: string;
        paddingTop: string;
        paddingBottom: string;
    },
    options: {
        textSize: { value: string; label: string }[];
        textAlign: { value: string; label: string }[];
        maxWidth: { value: string; label: string }[];
        elementAlign: { value: string; label: string }[];
        paddingTop: { value: string; label: string }[];
        paddingBottom: { value: string; label: string }[];
    }
): BlockFieldConfig<HeadingFormatting> {
    return {
        label,
        group: "formatting",
        fields: [
            {
                value: "textSize",
                type: "select",
                label: fieldLabels.textSize,
                options: options.textSize,
            } satisfies BlockDefinitionSelectField,
            {
                value: "textAlign",
                type: "select",
                label: fieldLabels.textAlign,
                options: options.textAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "maxWidth",
                type: "select",
                label: fieldLabels.maxWidth,
                options: options.maxWidth,
            } satisfies BlockDefinitionSelectField,
            {
                value: "elementAlign",
                type: "select",
                label: fieldLabels.elementAlign,
                options: options.elementAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingTop",
                type: "select",
                label: fieldLabels.paddingTop,
                options: options.paddingTop,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingBottom",
                type: "select",
                label: fieldLabels.paddingBottom,
                options: options.paddingBottom,
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: {
                textSize: defaultTextSize,
                textAlign: defaultTextAlign,
                maxWidth: defaultMaxWidth,
                elementAlign: defaultElementAlign,
                paddingTop: defaultPaddingTop,
                paddingBottom: defaultPaddingBottom,
            },
            isHidden: false,
        },
    };
}

function separateButtonsLayoutField(
    label: string,
    defaultTextSize: ButtonsFormatting["textSize"] = "text-sm",
    defaultMaxWidth: ButtonsFormatting["maxWidth"] = "max-w-2xl",
    defaultElementAlign: ButtonsFormatting["elementAlign"] = "mx-auto",
    defaultPaddingTop: ButtonsFormatting["paddingTop"] = "pt-4",
    defaultPaddingBottom: ButtonsFormatting["paddingBottom"] = "pb-4",
    defaultJustifyContent: ButtonsFormatting["justifyContent"] = "justify-start",
    fieldLabels: {
        textSize: string;
        maxWidth: string;
        elementAlign: string;
        paddingTop: string;
        paddingBottom: string;
        justifyContent: string;
    },
    options: {
        textSize: { value: string; label: string }[];
        maxWidth: { value: string; label: string }[];
        elementAlign: { value: string; label: string }[];
        paddingTop: { value: string; label: string }[];
        paddingBottom: { value: string; label: string }[];
        justifyContent: { value: string; label: string }[];
    }
): BlockFieldConfig<ButtonsFormatting> {
    return {
        label,
        group: "formatting",
        fields: [
            {
                value: "textSize",
                type: "select",
                label: fieldLabels.textSize,
                options: options.textSize,
            } satisfies BlockDefinitionSelectField,
            {
                value: "maxWidth",
                type: "select",
                label: fieldLabels.maxWidth,
                options: options.maxWidth,
            } satisfies BlockDefinitionSelectField,
            {
                value: "justifyContent",
                type: "select",
                label: fieldLabels.justifyContent,
                options: options.justifyContent,
            } satisfies BlockDefinitionSelectField,
            {
                value: "elementAlign",
                type: "select",
                label: fieldLabels.elementAlign,
                options: options.elementAlign,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingTop",
                type: "select",
                label: fieldLabels.paddingTop,
                options: options.paddingTop,
            } satisfies BlockDefinitionSelectField,
            {
                value: "paddingBottom",
                type: "select",
                label: fieldLabels.paddingBottom,
                options: options.paddingBottom,
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: {
                textSize: defaultTextSize,
                maxWidth: defaultMaxWidth,
                elementAlign: defaultElementAlign,
                paddingTop: defaultPaddingTop,
                paddingBottom: defaultPaddingBottom,
                justifyContent: defaultJustifyContent,
            },
            isHidden: false,
        },
    };
}

function linkTextField(
    label: string,
    defaultData: BlockLink & BlockText,
    fieldLabels: {
        text: string;
        label: string;
        href: string;
    }
): BlockFieldConfig<BlockLink & BlockText> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "text",
                label: fieldLabels.text,
            } satisfies BlockDefinitionTextField,
            {
                value: "label",
                type: "text",
                label: fieldLabels.label,
            } satisfies BlockDefinitionTextField,
            {
                value: "href",
                type: "link",
                label: fieldLabels.href,
            } satisfies BlockDefinitionLinkField,
        ],
        defaultData: {
            data: defaultData,
            isHidden: false,
        },
    };
}

function arrayField<T extends Record<string, string | boolean>>(
    label: string,
    group: BlockDefinitionGroup,
    fields: {
        [K in keyof T]: {
            label: string;
            type?: string;
            options?: { value: string; label: string }[];
        };
    },
    defaultItems: T[],
    newItem: T
): BlockFieldConfig<BlockArrayItems<T>> {
    return {
        label,
        group,
        fields: Object.entries(fields).map(([key, value]) => ({
            value: key,
            type: value.type ?? "text",
            label: value.label,
            ...(value.type === "select" && {
                options: value.options,
            }),
        })) as BlockDefinitionField[],
        defaultData: {
            data: {
                items: defaultItems.map((item) => ({
                    ...item,
                    id: randomId(),
                })),
            },
            isHidden: false,
        },
        // @ts-expect-error TODO BF: fix typing
        newItem,
    };
}

export {
    createBlock,
    textField,
    htmlField,
    markdownField,
    headingField,
    separateMediaFormattingField,
    buttonField,
    imageField,
    descriptiveImageField,
    backgroundImageField,
    mediaField,
    linkTextField,
    arrayField,
    separateSimpleListFormattingField,
    separateButtonsLayoutField,
    separateHeadingFormattingField,
    separateParagraphFormattingField,
    separateInfoNoteFormattingField,
    separateTestimonialFormattingField,
    testimonialField,
};
